<template>
  <form @submit.prevent="addUserByAgent">
    <h3>{{ $t('userselection.title') }}</h3>

    <label
      for="selectUser"
      class="required-input"
    >{{ $t('userselection.user') }}</label>
    <Dropdown
      id="selectUser"
      v-model="selectedUser"
      :placeholder="$t('userselection.select')"
      :options="existingUsers"
      :option-label="(user) => `${user.firstname} ${user.lastname} (${user.company})`"
      class="col-12 mt-2 p-0"
      filter
      @change="v$.$reset()"
      @click="fetchUsers"
    />

    <div class="my-2">
      <b>{{ $t('userselection.or') }}</b>
    </div>

    <!-- Lastname -->
    <div class="p-input mb-2">
      <label
        for="lastname"
        class="required-input"
      >{{ $t('userselection.new') }}</label>
      <InputText
        id="lastname"
        v-model="v$.user.lastname.$model"
        :placeholder="$t('entity.user.lastname')"
        class="col-12 mt-2"
        :class="{'invalid-input': v$.user.lastname.$error }"
        type="text"
        required
      />
      <div
        v-for="error of v$.user.lastname.$errors"
        :key="error.$uid"
        class="input-errors"
      >
        <div class="error-msg">
          {{ error.$message }}
        </div>
      </div>
    </div>

    <!-- Firstname -->
    <div class="p-input mb-2">
      <InputText
        id="firstname"
        v-model="v$.user.firstname.$model"
        :placeholder="$t('entity.user.firstname')"
        class="col-12 mt-2"
        :class="{'invalid-input': v$.user.firstname.$error }"
        type="text"
        required
      />
      <div
        v-for="error of v$.user.firstname.$errors"
        :key="error.$uid"
        class="input-errors"
      >
        <div class="error-msg">
          {{ error.$message }}
        </div>
      </div>
    </div>

    <!-- Company -->
    <div class="p-input mb-2">
      <InputText
        id="company"
        v-model="v$.user.company.$model"
        :placeholder="$t('entity.user.company')"
        class="col-12 mt-2"
        :class="{'invalid-input': v$.user.company.$error }"
        type="text"
        autocomplete="organization"
      />
      <div
        v-for="error of v$.user.company.$errors"
        :key="error.$uid"
        class="input-errors"
      >
        <div class="error-msg">
          {{ error.$message }}
        </div>
      </div>
    </div>

    <!-- Email -->
    <div class="p-input mb-2">
      <InputText
        id="email"
        v-model="v$.user.email.$model"
        :placeholder="$t('entity.user.email')"
        class="col-12 mt-2"
        :class="{'invalid-input': v$.user.email.$error }"
        type="email"
        autocomplete="email"
        required
      />
      <div
        v-for="error of v$.user.email.$errors"
        :key="error.$uid"
        class="input-errors"
      >
        <div class="error-msg">
          {{ error.$message }}
        </div>
      </div>
    </div>

    <Message
      v-if="existingUserId"
      severity="info"
      :closable="false"
    >
      {{ $t('userselection.not_activated') }}
      <a
        class="cursor-pointer"
        @click="resendActivationMail"
      >
        {{ $t('userselection.resend_activation_link') }}
      </a>
    </Message>

    <Message
      v-if="errors.length > 0"
      severity="error"
      :closable="false"
    >
      <ul class="list-none m-0 p-0">
        <li
          v-for="error of errors"
          :key="error"
        >
          {{ $t(error) }}
        </li>
      </ul>
    </Message>

    <div class="my-3 flex justify-content-center flex-wrap">
      <Button
        v-show="!addButtonDisabled"
        type="submit"
        :label="$t('action.crud.create')"
        icon="pi pi-save"
        :loading="isSaving"
        class="p-button-rounded p-button-outlined"
      />
    </div>
  </form>
</template>

<script>
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Message from 'primevue/message';
import {useVuelidate} from '@vuelidate/core';
import {email, maxLength, required} from '@/i18n/i18nValidators';
import Dropdown from "primevue/dropdown";

export default {
  name: 'UserSelectionComponent',
  components: {Dropdown, InputText, Button, Message},
  props: {
    modelValue: {
      type: Object,
      default: null,
    },
    stakeholder: {
      type: Object,
      required: true
    }
  },
  emits: ['update:modelValue'],
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      existingUsers: [],
      user: {
        lastname: null,
        firstname: null,
        company: null,
        email: null,
        stakeholderId: null
      },
      isSaving: false,
      existingUserId: null,
      errors: []
    }
  },
  validations() {
    return {
      user: {
        lastname: {required, maxLength: maxLength(100)},
        firstname: {required, maxLength: maxLength(100)},
        company: {required, maxLength: maxLength(255)},
        email: {required, email}
      }
    }
  },
  computed: {
    addButtonDisabled() {
      return this.v$.$invalid;
    },
    selectedUser: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
  },
  methods: {
    async fetchUsers() {
      this.existingUsers = (await this.$userService.fetchUsersPagingAndSorting({
        "page": 0,
        "size": 100,
        "stakeholder": this.stakeholder.id,
        "locked": false,
        "activated": true
      })).content;
    },
    async addUserByAgent() {
      this.errors = [];
      this.existingUserId = null;

      const isFormValid = await this.v$.$validate();
      if (!isFormValid) return;

      this.isSaving = true;
      try {
        this.user.stakeholderId = this.stakeholder.id;
        /** @type {AgentCreateUserResponse} */
        const agentCreateUserResponse = await this.$userService.addUserByAgent(this.user);
        if (!agentCreateUserResponse.activated) {
          this.existingUserId = agentCreateUserResponse.user.id;
        } else {
          this.existingUsers.push(agentCreateUserResponse.user);
          this.selectedUser = agentCreateUserResponse.user;
          this.$emitter.emit('toast', {severity: 'success', message: 'userpage.createSuccess'});
          this.resetUser();
          this.v$.$reset();
        }
      } catch (e) {
        this.errors = e.response.data;
      } finally {
        this.isSaving = false;
      }
    },
    async resendActivationMail() {
      try {
        await this.$userService.resendActivationCode(this.existingUserId);
        this.$emitter.emit('toast', {severity: 'success', message: 'userselection.resend_success'});
        this.existingUserId = null;
        this.resetUser();
        this.v$.$reset();
      } catch (e) {
        const errors = e.response.data.map(error => this.$t(error)).join(', ');
        this.$emitter.emit('toast', {severity: 'error', message: errors});
      }
    },
    resetUser() {
      this.user = {
        lastname: null,
        firstname: null,
        company: null,
        email: null,
        stakeholderId: null
      };
    }
  }
}
</script>

